import Analytics from '@/utils/analytics';
import i18n from '@/utils/i18n';
import { useCallback, useMemo } from 'react';

import { ApolloError } from '@apollo/client';
import { captureException } from '@/utils/sentry';
import { getMessageFromGQLError } from '@/utils/apollo-client';
import {
  hasUnsavedQuantities,
  useNewOrderActionsContext,
} from '@/contexts/new-order-actions-context';
import { isDefaultCart } from '@/utils/new-order';
import { OrderDomain } from '@/types';
import { SelectedFulfillmentType } from '@/contexts/new-order-actions-context-types';
import { UNPLACED_ORDER_STATUSES } from '@domain/fulfillment-settings/blocs/order-availability';
import { useAuthenticatedUserContext } from '@/contexts/user-context';
import { useNewOrderContext } from '@/contexts/new-order-context';
import { useToasts } from '@/contexts/toast-context';
import { useUpdateFulfillment } from '@/hooks/use-update-fulfillment';

export type CartVisibility = 'PUBLIC' | 'PRIVATE';

const MAX_STOREFRONT_CARTS = 50;
export const VISIBLE_CART_CAP = 5;
export const MAX_CART_NAME_LENGTH = 36;
export const DEFAULT_CART_ID = '__DEFAULT_CART__';
const DEFAULT_CART_VISIBILITY: CartVisibility = 'PUBLIC';

export interface Cart {
  id: string;
  isDefault: boolean;
  name: string;
  orderUUID: string | null;
  visibility: CartVisibility;
}

interface CartMutationInput {
  fulfillment?: SelectedFulfillmentType | null;
  name: string;
  visibility: CartVisibility;
}

export const isReservedCartName = (name: string) =>
  name.toLowerCase().replace(/\s/g, '') ===
  i18n.t('multi_cart.default_cart').toLowerCase().replace(/\s/g, '');

const getCartActionErrorMessage = (e: unknown) =>
  (e instanceof ApolloError ? getMessageFromGQLError(e) : null) ??
  i18n.t('toast.cart_action_failed');

export const useCartActions = () => {
  const {
    selectedRestaurantUUID: restaurantUUID,
    selectedRestaurantRelationship: { supplierUUID },
  } = useAuthenticatedUserContext();
  const { showErrorToast } = useToasts();
  const {
    cancelPendingInputs,
    createOrder,
    deleteOrder,
    nextAvailableFulfillment,
    selectFulfillment,
    updateAndClearQuantity,
    updateOrderMetadata,
  } = useNewOrderActionsContext();
  const { updateFulfillment } = useUpdateFulfillment({
    restaurantUUID,
    supplierUUID,
  });
  const { draftOrders, draftOrder, switchActiveOrder } = useNewOrderContext();

  const { activeCart, carts } = useMemo(() => {
    const defaultName = i18n.t('multi_cart.default_cart');
    let defaultCart: Cart | null = null;
    let activeNamedCart: Cart | null = null;
    const otherCarts: Cart[] = [];

    for (const order of draftOrders) {
      const { name, visibility } = order.storefront_metadata ?? {};
      const cart: Cart = {
        id: order.uuid,
        isDefault: isDefaultCart(order),
        name: name || defaultName,
        orderUUID: order.uuid,
        visibility: (visibility ?? DEFAULT_CART_VISIBILITY) as CartVisibility,
      };
      if (cart.isDefault) {
        defaultCart = cart;
      } else if (cart.orderUUID === draftOrder?.uuid) {
        activeNamedCart = cart;
      } else {
        otherCarts.push(cart);
      }
    }

    const resolvedDefaultCart = defaultCart ?? {
      id: DEFAULT_CART_ID,
      isDefault: true,
      name: defaultName,
      orderUUID: null,
      visibility: DEFAULT_CART_VISIBILITY,
    };

    return {
      activeCart: activeNamedCart ?? resolvedDefaultCart,
      carts: [
        resolvedDefaultCart,
        ...(activeNamedCart ? [activeNamedCart] : []),
        ...otherCarts,
      ],
    };
  }, [draftOrders, draftOrder?.uuid]);

  const visibleCarts = useMemo(() => carts.slice(0, VISIBLE_CART_CAP), [carts]);

  const showCartLimitToast = useCallback(
    () =>
      showErrorToast(
        i18n.t('multi_cart.cart_limit_reached', {
          count: MAX_STOREFRONT_CARTS,
        }),
      ),
    [showErrorToast],
  );

  const createCart = useCallback(
    async ({ fulfillment, name, visibility }: CartMutationInput) => {
      const flushed = await updateAndClearQuantity('cart_switch');
      if (hasUnsavedQuantities(flushed)) {
        return false;
      }
      const nextFulfillment = fulfillment ?? nextAvailableFulfillment;
      try {
        const result = await createOrder({
          variables: {
            deliveryDate: nextFulfillment?.date,
            fulfillmentType: nextFulfillment?.type,
            name: name.trim(),
            orderDomain: OrderDomain.Storefront,
            restaurantUUID,
            supplierUUID,
            timeslotEnd: nextFulfillment?.timeslot?.endTime,
            timeslotStart: nextFulfillment?.timeslot?.startTime,
            visibility,
          },
        });
        const createdUUID = result.data?.createOrder?.order?.uuid;
        if (createdUUID) {
          await switchActiveOrder(createdUUID);
        }
        Analytics.track('multi_cart.cart.create', {
          hasCustomFulfillment: Boolean(fulfillment),
          visibility,
        });
        return true;
      } catch (e) {
        captureException(e);
        showErrorToast(getCartActionErrorMessage(e));
        return false;
      }
    },
    [
      nextAvailableFulfillment,
      restaurantUUID,
      supplierUUID,
      createOrder,
      showErrorToast,
      switchActiveOrder,
      updateAndClearQuantity,
    ],
  );

  const editCart = useCallback(
    async (
      cartUUID: string,
      { fulfillment, name, visibility }: CartMutationInput,
    ) => {
      const metadata = draftOrders.find(
        (order) => order.uuid === cartUUID,
      )?.storefront_metadata;
      const trimmedName = name.trim();
      const renamed = !metadata || metadata.name !== trimmedName;
      const changedVisibility = !metadata || metadata.visibility !== visibility;
      const metadataChanged = renamed || changedVisibility;
      if (!fulfillment && !metadataChanged) {
        return true;
      }

      try {
        if (fulfillment) {
          await updateFulfillment({
            variables: {
              orderUUID: cartUUID,
              set: {
                fulfillment_timeslot_end: fulfillment.timeslot?.endTime ?? null,
                fulfillment_timeslot_start:
                  fulfillment.timeslot?.startTime ?? null,
                fulfillment_type: fulfillment.type,
                restaurant_desired_delivery_time: fulfillment.date,
              },
              unplacedOrderStatuses: UNPLACED_ORDER_STATUSES,
            },
          });
          if (cartUUID === draftOrder?.uuid) {
            selectFulfillment(fulfillment);
          }
        }
        if (metadataChanged) {
          await updateOrderMetadata({
            variables: { name: trimmedName, orderUUID: cartUUID, visibility },
          });
        }
        Analytics.track('multi_cart.cart.update', {
          changedFulfillment: Boolean(fulfillment),
          changedVisibility,
          renamed,
          visibility,
        });
        return true;
      } catch (e) {
        captureException(e);
        showErrorToast(getCartActionErrorMessage(e));
        return false;
      }
    },
    [
      draftOrders,
      draftOrder?.uuid,
      selectFulfillment,
      updateOrderMetadata,
      updateFulfillment,
      showErrorToast,
    ],
  );

  const deleteCart = useCallback(
    async (cartUUID: string) => {
      const wasActive = draftOrder?.uuid === cartUUID;
      if (wasActive) {
        cancelPendingInputs();
      }
      try {
        const result = await deleteOrder({
          variables: { orderUUID: cartUUID },
        });
        if (!result.data?.deleteOrder.success) {
          showErrorToast(getCartActionErrorMessage(null));
          return false;
        }
        if (wasActive) {
          await switchActiveOrder(null);
        }
        Analytics.track('multi_cart.cart.delete', { wasActive });
        return true;
      } catch (e) {
        captureException(e);
        showErrorToast(getCartActionErrorMessage(e));
        return false;
      }
    },
    [
      cancelPendingInputs,
      deleteOrder,
      draftOrder?.uuid,
      switchActiveOrder,
      showErrorToast,
    ],
  );

  const selectCart = useCallback(
    async (cart: Cart) => {
      const flushed = await updateAndClearQuantity('cart_switch');
      if (hasUnsavedQuantities(flushed)) {
        return false;
      }
      try {
        await switchActiveOrder(cart.isDefault ? null : cart.orderUUID);
      } catch (e) {
        captureException(e);
        showErrorToast(getCartActionErrorMessage(e));
        return false;
      }
      Analytics.track('multi_cart.cart.select', { isDefault: cart.isDefault });
      return true;
    },
    [switchActiveOrder, updateAndClearQuantity, showErrorToast],
  );

  return {
    activeCart,
    atCap: carts.length - 1 >= MAX_STOREFRONT_CARTS,
    carts,
    createCart,
    deleteCart,
    editCart,
    hasOverflow: carts.length > VISIBLE_CART_CAP,
    selectCart,
    showCartLimitToast,
    visibleCarts,
  };
};
